import { useState, useEffect, useRef, useMemo } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { PDFToolbar } from "./PDFToolbar";
import { ThumbnailPanel } from "./ThumbnailPanel";
import { AnnotationPanel, AnnotationConfig } from "./AnnotationPanel";
import { AnnotationLayer } from "./AnnotationLayer";
import { BookmarkPanel } from "./BookmarkPanel";
import { BookmarkSticker } from "./BookmarkSticker";
import { useIsMobile } from "@/hooks/use-mobile";
import Hammer from "@egjs/hammerjs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Annotation,
  Bookmark,
  saveAnnotation,
  getAnnotations,
  deleteAnnotation,
  saveBookmark,
  getBookmarks,
  deleteBookmark,
  saveOutline,
  getOutline,
} from "@/lib/indexedDB";
import {
  saveReadingProgress,
  loadReadingProgress,
} from "@/lib/readingProgress";
import { extractHeadingsFromText, extractNativeOutline, OutlineItem } from "@/lib/pdfHeadingExtract";
import { toast } from "sonner";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PDFViewerProps {
  url?: string;
}

export const PDFViewer = ({ url = "./sample.pdf" }: PDFViewerProps) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const isMobile = useIsMobile();
  const [baseScale, setBaseScale] = useState<number>(1);
  const [zoomFactor, setZoomFactor] = useState<number>(1); // 1=100%、1.25=125%...
  const [displayZoom, setDisplayZoom] = useState<number>(1); // 顯示用的即時縮放
  const scale = useMemo(
    () => Number((baseScale * zoomFactor).toFixed(4)),
    [baseScale, zoomFactor]
  );
  const zoomPercent = Math.round(displayZoom * 100);
  const [displayMode, setDisplayMode] = useState<"single" | "double">("single");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [toolbarVisible, setToolbarVisible] = useState(true);
  const [thumbnailOpen, setThumbnailOpen] = useState(false);
  const [annotationPanelOpen, setAnnotationPanelOpen] = useState(false);
  const [bookmarkPanelOpen, setBookmarkPanelOpen] = useState(false);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [pdfOutline, setPdfOutline] = useState<OutlineItem[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [annotationConfig, setAnnotationConfig] =
    useState<AnnotationConfig | null>({
      type: "highlight",
      color: "yellow",
      opacity: 30,
    });
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const pageRef = useRef<HTMLDivElement>(null);
  const [vw, setVw] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );
  const [vh, setVh] = useState(
    typeof window !== "undefined" ? window.innerHeight : 0
  );
  // 單頁模式：以高度為基準，雙頁模式：每頁寬度 = (總寬度 - 間距) / 2
  const pageDims = isMobile ? { width: vw } : { height: vh }; // 單頁用 height = window.innerHeight

  const containerRef = useRef<HTMLDivElement>(null);
  const mainViewerRef = useRef<HTMLDivElement>(null); // 主容器 ref
  const zoomFactorRef = useRef<number>(1); // 追蹤最新的 zoomFactor

  const isZoomed = scale > 1.001;

  const pageWrapperRef = useRef<HTMLDivElement>(null);
  const isGesturingRef = useRef(false);
  const isDrawingRef = useRef(false);

  const isPagingRef = useRef(false); // 翻頁中

  // 同步 zoomFactor 到 ref 和 displayZoom
  useEffect(() => {
    zoomFactorRef.current = zoomFactor;
    setDisplayZoom(zoomFactor);
  }, [zoomFactor]);

  // 同步 isDrawing 到 ref
  useEffect(() => {
    isDrawingRef.current = isDrawing;
  }, [isDrawing]);

  // Auto-hide toolbar after inactivity
  useEffect(() => {
    let timer: NodeJS.Timeout;
    let isToolbarActive = false;

    const resetTimer = () => {
      // setToolbarVisible(true);
      clearTimeout(timer);
      timer = setTimeout(() => setToolbarVisible(false), 3000);
    };

    // 使用者在 toolbar 上操作時，暫停倒數
    const handleToolbarEnter = () => {
      isToolbarActive = true;
      clearTimeout(timer);
    };

    const handleToolbarLeave = () => {
      isToolbarActive = false;
      resetTimer();
    };

    window.addEventListener("mousemove", resetTimer);
    window.addEventListener("touchstart", resetTimer);
    resetTimer();

    const hudEls = Array.from(
      document.querySelectorAll<HTMLElement>(".toolbar, .progressbar")
    );
    return () => {
      window.removeEventListener("mousemove", resetTimer);
      window.removeEventListener("touchstart", resetTimer);
      window.removeEventListener("click", resetTimer);
      hudEls.forEach((el) => {
        el.removeEventListener("mouseenter", handleToolbarEnter);
        el.removeEventListener("mouseleave", handleToolbarLeave);
      });
      clearTimeout(timer);
    };
  }, []);

  // Responsive display mode
  // 及時監控螢幕大小
  useEffect(() => {
    const update = () => {
      requestAnimationFrame(() => {
        // 使用 screen 尺寸而不是 visualViewport，避免鍵盤影響
        const w = window.innerWidth;
        const h = window.innerHeight;
        setVw(w);
        setVh(h);
        setDisplayMode(w > h ? "double" : "single");
      });
    };

    update();
    window.addEventListener("resize", update, { passive: true });
    window.addEventListener("orientationchange", update, { passive: true });

    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPage(pageNumber - (displayMode === "double" ? 2 : 1));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goToPage(pageNumber + (displayMode === "double" ? 2 : 1));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, displayMode]);

  // 重新繪製canvas容器大小
  useEffect(() => {
    // 以 DOM 實測目前 Page 的主畫布尺寸（react-pdf 會重繪 canvas）
    const root = pageRef.current;
    if (!root) return;

    // 使用 setTimeout 確保在 Page 渲染完成後才查詢
    const timer = setTimeout(() => {
      // react-pdf 會先畫一個主要 canvas（頁面位圖）
      // 只查詢 react-pdf 的 canvas，排除 AnnotationLayer 的 canvas
      const canvas = root.querySelector(".react-pdf__Page__canvas");
      if (canvas) {
        const { width, height } = canvas.getBoundingClientRect();
        setPageSize({
          width: Math.round(width),
          height: Math.round(height),
        });
      }
    }, 0);

    return () => clearTimeout(timer);
    // 若你有渲染 TextLayer/AnnotationLayer（react-pdf 的），也會隨 scale 一起更新
    // 我們以最底的 canvas 為基準即可
  }, [scale, vw, vh, pageNumber, displayMode]);

  const commitZoomKeepingCenter = (newScale: number) => {
    if (isPagingRef.current) return;
    const c = containerRef.current;
    const oldScale = zoomFactorRef.current;
    const ratio = newScale / oldScale;

    if (!c) {
      setZoomFactor(newScale);
      return;
    }

    const cx = c.scrollLeft + c.clientWidth / 2;
    const cy = c.scrollTop + c.clientHeight / 2;

    setZoomFactor(newScale);

    requestAnimationFrame(() => {
      c.scrollLeft = cx * ratio - c.clientWidth / 2;
      c.scrollTop = cy * ratio - c.clientHeight / 2;
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Pinch 手勢狀態
    let isPinching = false;
    let startDistance = 0;
    let startScale = 1;
    let currentScale = 1;
    let centerX = 0; // 縮放中心點 X
    let centerY = 0; // 縮放中心點 Y

    // 計算兩指間的距離
    const getDistance = (touch1: Touch, touch2: Touch) => {
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    // 計算兩指的中心點（在視口中的絕對位置）
    const getCenter = (touch1: Touch, touch2: Touch) => {
      return {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };
    };

    const handleTouchStart = (e: TouchEvent) => {
      // 只處理雙指觸控
      if (e.touches.length === 2) {
        isPinching = true;
        isGesturingRef.current = true;
        startDistance = getDistance(e.touches[0], e.touches[1]);
        startScale = zoomFactorRef.current;
        currentScale = startScale;

        // 記錄兩指中心點作為縮放中心
        const center = getCenter(e.touches[0], e.touches[1]);
        centerX = center.x;
        centerY = center.y;

        if (pageWrapperRef.current) {
          // 計算相對於 pageWrapper 的百分比位置
          const wrapperRect = pageWrapperRef.current.getBoundingClientRect();

          // 將視口座標轉換為相對於 wrapper 的座標
          const relativeX =
            ((centerX - wrapperRect.left) / wrapperRect.width) * 100;
          const relativeY =
            ((centerY - wrapperRect.top) / wrapperRect.height) * 100;

          // 設置縮放原點為兩指中心
          pageWrapperRef.current.style.transformOrigin = `${relativeX}% ${relativeY}%`;
          pageWrapperRef.current.style.willChange = "transform";
        }
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPinching || e.touches.length !== 2) return;
      if (!pageWrapperRef.current) return;

      e.preventDefault();

      const currentDistance = getDistance(e.touches[0], e.touches[1]);
      const scaleChange = currentDistance / startDistance;

      // 計算新的縮放比例，限制在 25%-400%
      const newScale = Math.min(Math.max(startScale * scaleChange, 0.25), 4);
      currentScale = newScale;

      // 即時更新 toolbar 顯示的百分比
      setDisplayZoom(newScale);

      // 視覺縮放 wrapper
      pageWrapperRef.current.style.transform = `scale(${newScale})`;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // 如果不再有兩指觸控，結束 pinch
      if (e.touches.length < 2 && isPinching) {
        // 清除手勢狀態
        isPinching = false;
        isGesturingRef.current = false;

        if (pageWrapperRef.current) {
          pageWrapperRef.current.style.transform = "";
          pageWrapperRef.current.style.transformOrigin = "";
          pageWrapperRef.current.style.willChange = "auto";
        }

        // 最終確定縮放
        setZoomFactor(currentScale);
        setDisplayZoom(currentScale);

        // 清除狀態變數
        startDistance = 0;
        centerX = 0;
        centerY = 0;
      }
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });
    container.addEventListener("touchcancel", handleTouchEnd, {
      passive: true,
    });

    // Hammer.js 只用於 doubletap
    const hammer = new Hammer.Manager(container);

    const doubleTap = new Hammer.Tap({
      event: "doubletap",
      taps: 2,
      interval: 300,
      threshold: 10,
      posThreshold: 30,
    });

    hammer.add([doubleTap]);

    // 雙擊重置縮放到 100%
    hammer.on("doubletap", (e) => {
      if (isPagingRef.current) return;

      e.preventDefault();
      e.srcEvent?.stopPropagation();

      const newScale = 1; // 重置為 100%
      setZoomFactor(newScale);
      setDisplayZoom(newScale);
    });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchcancel", handleTouchEnd);
      hammer.destroy();
    };
  }, []);

  const onDocumentLoadSuccess = async ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    // 載入PDF閱讀進度
    const savedPage = loadReadingProgress(url);
    if (savedPage && savedPage <= numPages) {
      setPageNumber(savedPage);
    }

    // 優先載入已儲存的目錄
    const savedOutline = await getOutline(url);

    if (savedOutline && savedOutline.items.length > 0) {
      setPdfOutline(savedOutline.items);
      toast.success(`已載入 ${savedOutline.items.length} 個章節`);
    } else {
      // 嘗試從 PDF 原生大綱提取
      toast.info("正在讀取目錄...");
      const nativeOutline = await extractNativeOutline(url);

      if (nativeOutline.length > 0) {
        setPdfOutline(nativeOutline);
        toast.success(`已載入 PDF 原生目錄（${nativeOutline.length} 個章節）`);
      } else {
        // 如果沒有原生大綱，使用字體大小啟發式提取
        toast.info("正在分析章節結構...");
        const outline = await extractHeadingsFromText(url);
        setPdfOutline(outline);

        if (outline.length > 0) {
          toast.success(`已自動提取 ${outline.length} 個章節`);
        } else {
          toast.info("未找到章節，可手動新增目錄");
        }
      }
    }

    // Load annotations and bookmarks
    // getBookmarks讀取PDF書籤,setBookmarks更新在畫面上
    const loadedAnnotations = await getAnnotations(url);
    const loadedBookmarks = await getBookmarks(url);
    setAnnotations(loadedAnnotations);
    setBookmarks(loadedBookmarks);
  };

  const goToPage = (page: number) => {
    const newPage = Math.max(1, Math.min(page, numPages));
    setPageNumber(newPage);
    saveReadingProgress(url, newPage);
  };

  // 縮放比例25% Min:25% Max:400%
  const STEP = 0.25; // 25%
  const MIN = 0.25; // 25%
  const MAX = 4.0; // 400%
  const roundToStep = (v: number) => Math.round(v / STEP) * STEP;
  const handleZoomIn = () => {
    const container = containerRef.current;
    const oldScale = zoomFactor;
    const newScale = Math.min(roundToStep(oldScale + STEP), MAX);

    setZoomFactor(newScale);
    setDisplayZoom(newScale);

    // 如果是第一次放大（從居中到左上對齊的轉換）
    if (oldScale <= 1 && newScale > 1 && container) {
      requestAnimationFrame(() => {
        if (container) {
          // 計算內容區域的大小
          const content = container.querySelector(".react-pdf__Document");
          if (content) {
            const contentRect = content.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            // 將內容置中
            container.scrollLeft =
              (contentRect.width - containerRect.width) / 2;
            container.scrollTop =
              (contentRect.height - containerRect.height) / 2;
          }
        }
      });
    } else if (newScale > 1 && container) {
      // 已經在放大狀態，保持中心點
      const scaleRatio = newScale / oldScale;
      const centerX = container.scrollLeft + container.clientWidth / 2;
      const centerY = container.scrollTop + container.clientHeight / 2;

      requestAnimationFrame(() => {
        if (container) {
          container.scrollLeft =
            centerX * scaleRatio - container.clientWidth / 2;
          container.scrollTop =
            centerY * scaleRatio - container.clientHeight / 2;
        }
      });
    }
  };

  const handleZoomOut = () => {
    const container = containerRef.current;
    if (container && isZoomed) {
      const oldScale = zoomFactor;
      const newScale = Math.max(roundToStep(zoomFactor - STEP), MIN);

      const scaleRatio = newScale / oldScale;

      const centerX = container.scrollLeft + container.clientWidth / 2;
      const centerY = container.scrollTop + container.clientHeight / 2;

      setZoomFactor(newScale);
      setDisplayZoom(newScale);

      requestAnimationFrame(() => {
        if (container) {
          container.scrollLeft =
            centerX * scaleRatio - container.clientWidth / 2;
          container.scrollTop =
            centerY * scaleRatio - container.clientHeight / 2;
        }
      });
    } else {
      const newScale = Math.max(roundToStep(zoomFactor - STEP), MIN);
      setZoomFactor(newScale);
      setDisplayZoom(newScale);
    }
  };

  const toggleDisplayMode = () => {
    setDisplayMode((prev) => (prev === "single" ? "double" : "single"));
  };

  const toggleFullscreen = () => {
    // 使用主容器 ref
    const viewer = mainViewerRef.current;

    if (!viewer) {
      console.error("Viewer element not found!");
      toast.error("無法找到閱讀器元素");
      return;
    }

    // 桌面平台使用原生 Fullscreen API
    if (!document.fullscreenElement) {
      const element = viewer || document.documentElement;
      if (element.requestFullscreen) {
        element
          .requestFullscreen()
          .then(() => {
            setIsFullscreen(true);
          })
          .catch((err) => {
            console.error("進入全螢幕失敗:", err);
            toast.error("無法進入全螢幕模式");
          });
      }
    } else {
      document
        .exitFullscreen()
        .then(() => {
          setIsFullscreen(false);
        })
        .catch((err) => {
          console.error("退出全螢幕失敗:", err);
        });
    }
    setTimeout(handlePageClick, 300);
  };

  const handleAnnotationStart = (config: AnnotationConfig) => {
    setAnnotationConfig(config);
    setIsDrawing(true);
    toast.success("在 PDF 上選擇要標註的區域");
  };

  const handleAnnotationCreate = async (
    annotation: Omit<Annotation, "id" | "pdfUrl">
  ) => {
    const newAnnotation: Annotation = {
      ...annotation,
      id: Date.now().toString(),
      pdfUrl: url,
      currentScreen: pageSize.width, // 保存創建時的頁面寬度
    };
    await saveAnnotation(newAnnotation);
    setAnnotations([...annotations, newAnnotation]);
    setIsDrawing(false);
    toast.success("標註已建立");
  };

  const handleAnnotationDelete = async (id: string) => {
    await deleteAnnotation(id);
    setAnnotations(annotations.filter((a) => a.id !== id));
    toast.success("標註已刪除");
  };

  const handleAnnotationUpdate = async (id: string, note: string) => {
    const annotation = annotations.find((a) => a.id === id);
    if (annotation) {
      const updated = { ...annotation, note };
      await saveAnnotation(updated);
      setAnnotations(annotations.map((a) => (a.id === id ? updated : a)));
      toast.success("標註已更新");
    }
  };

  // 新增書籤
  const handleBookmarkCreate = async (
    pageNumber: number,
    color: "red" | "yellow" | "green" | "blue"
  ) => {
    // 檢查該頁面是否已有書籤
    const existingBookmark = bookmarks.find((b) => b.pageNumber === pageNumber);

    if (existingBookmark) {
      // 刪除舊書籤
      await deleteBookmark(existingBookmark.id);
    }

    const newBookmark: Bookmark = {
      id: Date.now().toString(),
      pageNumber,
      color,
      createdAt: Date.now(),
      pdfUrl: url,
    };
    await saveBookmark(newBookmark);

    // 更新書籤列表：移除舊的（如果有），加入新的
    const updatedBookmarks = existingBookmark
      ? bookmarks.filter((b) => b.id !== existingBookmark.id)
      : bookmarks;
    setBookmarks([...updatedBookmarks, newBookmark]);

    toast.success(
      existingBookmark
        ? `已更新第 ${pageNumber} 頁的書籤`
        : `已為第 ${pageNumber} 頁新增書籤`
    );
  };

  // 刪除書籤
  const handleBookmarkDelete = async (id: string) => {
    await deleteBookmark(id);
    setBookmarks(bookmarks.filter((b) => b.id !== id));
    toast.success("書籤已刪除");
  };

  // 更新書籤
  const handleBookmarkUpdate = async (
    id: string,
    note: string,
    color?: "red" | "yellow" | "green" | "blue"
  ) => {
    const bookmark = bookmarks.find((b) => b.id === id);
    if (bookmark) {
      const updated = { ...bookmark, note, color };
      await saveBookmark(updated);
      setBookmarks(bookmarks.map((b) => (b.id === id ? updated : b)));
      toast.success("書籤已更新");
    }
  };

  const handlePageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Toggle toolbar visibility on click
    setToolbarVisible((prev) => !prev);
  };

  // === 點擊左右遮罩換頁 ===
  const pageStep = displayMode === "double" ? 2 : 1;

  // 確保雙頁模式起始頁為奇數頁
  const alignSpreadStart = (p: number) =>
    displayMode === "double" ? (p % 2 === 0 ? p - 1 : p) : p;

  const handlePrevPage = (e: React.MouseEvent) => {
    if (scale > 1) {
      isPagingRef.current = true;
    }

    e.stopPropagation();
    const target = alignSpreadStart(pageNumber - pageStep);
    goToPage(target);
    setTimeout(() => {
      isPagingRef.current = false;
    }, 3000);
  };

  const handleNextPage = (e: React.MouseEvent) => {
    if (scale > 1) {
      isPagingRef.current = true;
    }
    e.stopPropagation();
    const target = alignSpreadStart(pageNumber + pageStep);
    goToPage(target);
    setTimeout(() => {
      isPagingRef.current = false;
    }, 3000);
  };

  const handleAnnotationToggle = () => {
    setAnnotationPanelOpen((prev) => {
      const next = !prev;
      // 若面板即將關閉，同時關閉標註模式
      if (!next && isDrawing) setIsDrawing(false);
      return next;
    });
  };

  // 處理目錄更新
  const handleOutlineUpdate = async (newOutline: OutlineItem[]) => {
    setPdfOutline(newOutline);
    await saveOutline({
      pdfUrl: url,
      items: newOutline,
      updatedAt: Date.now(),
    });
    toast.success("目錄已更新");
  };

  return (
    <div
      ref={mainViewerRef}
      className="relative flex h-[100dvh] flex-col overflow-hidden bg-[hsl(var(--reader-bg))]"
    >
      {/* 浮水印 */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-50 rounded-md bg-background/10 px-3 py-1 text-xs text-muted-foreground backdrop-blur-sm pointer-events-none whitespace-nowrap">
        © Author - 王大明 | Reader - {"張華珊"}
      </div>

      {/* Panels */}
      <ThumbnailPanel
        isOpen={thumbnailOpen}
        onClose={() => setThumbnailOpen(false)}
        pdfUrl={url}
        numPages={numPages}
        currentPage={pageNumber}
        onPageSelect={goToPage}
        outline={pdfOutline}
        onOutlineUpdate={handleOutlineUpdate}
        enableEdit={true} // 設為 true 啟用編輯（業主模式）
      />

      <AnnotationPanel
        isOpen={annotationPanelOpen}
        onClose={() => setAnnotationPanelOpen(false)}
        onAnnotationStart={handleAnnotationStart}
        annotations={annotations}
        currentPage={pageNumber}
        onAnnotationDelete={handleAnnotationDelete}
        onAnnotationUpdate={handleAnnotationUpdate}
        onAnnotationNavigate={goToPage}
      />

      <BookmarkPanel
        isOpen={bookmarkPanelOpen}
        onClose={() => setBookmarkPanelOpen(false)}
        bookmarks={bookmarks}
        currentPage={pageNumber}
        onBookmarkCreate={handleBookmarkCreate}
        onBookmarkDelete={handleBookmarkDelete}
        onBookmarkUpdate={handleBookmarkUpdate}
        onBookmarkNavigate={goToPage}
      />
      {/* visible={toolbarVisible} */}
      {/* Toolbar */}
      <PDFToolbar
        className="toolbar hud"
        visible={toolbarVisible}
        currentPage={pageNumber}
        totalPages={numPages}
        zoomPercent={zoomPercent}
        displayMode={displayMode}
        isFullscreen={isFullscreen}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onPageChange={goToPage}
        onDisplayModeToggle={toggleDisplayMode}
        onFullscreenToggle={toggleFullscreen}
        onThumbnailToggle={() => setThumbnailOpen(true)}
        onAnnotationToggle={handleAnnotationToggle}
        onBookmarkToggle={() => setBookmarkPanelOpen(true)}
      />

      {/* PDF Container */}
      <div
        ref={containerRef}
        className="flex-1 relative "
        onClick={handlePageClick}
        onContextMenu={(e) => e.preventDefault()}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          commitZoomKeepingCenter(1);
        }}
        style={{ touchAction: isDrawing ? "none" : "pan-x pan-y" }}
      >
        {/* 左右換頁遮罩層 未標註時啟用）*/}
        {/* 雙頁模式時變成按鈕 */}
        {!isDrawing && (
          <div
            className={
              scale <= 1
                ? "absolute inset-0 z-30 flex"
                : "fixed inset-0 z-30 flex justify-between items-end pb-20 px-5 pointer-events-none"
            }
          >
            {/* 左半邊 - 上一頁 */}
            <button
              onClick={(e) => {
                e.preventDefault();
                handlePrevPage(e);
              }}
              className={
                scale <= 1
                  ? "w-1/3 cursor-pointer bg-transparent transition-colors outline-none border-none select-none"
                  : "pointer-events-auto w-12 h-12 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-sm flex items-center justify-center transition-all cursor-pointer border-none outline-none"
              }
              title="上一頁"
            >
              {scale > 1 && <ChevronLeft className="w-6 h-6 text-white" />}
            </button>
            {/* 中間 - 雙擊重置縮放 */}
            {scale <= 1 && (
              <button className="w-1/3 cursor-default bg-transparent outline-none border-none select-none" />
            )}
            {/* 右半邊 - 下一頁 */}
            <button
              onClick={(e) => {
                e.preventDefault();
                handleNextPage(e);
              }}
              className={
                scale <= 1
                  ? "w-1/3 cursor-pointer bg-transparent transition-colors outline-none border-none select-none"
                  : "pointer-events-auto w-12 h-12 rounded-full bg-black/30 hover:bg-black/50 backdrop-blur-sm flex items-center justify-center transition-all cursor-pointer border-none outline-none"
              }
              title="下一頁"
            >
              {scale > 1 && <ChevronRight className="w-6 h-6 text-white" />}
            </button>
          </div>
        )}

        <div className="flex min-h-full items-center justify-center ">
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            className="flex gap-4 w-full h-full justify-center "
            loading={
              <div className="flex h-96 items-center justify-center">
                <div className="text-muted-foreground">載入 PDF 中...</div>
              </div>
            }
            error={
              <div className="flex h-96 flex-col items-center justify-center gap-4 text-center">
                <div className="rounded-lg bg-card p-6 shadow-lg">
                  <h2 className="mb-2 text-xl font-semibold text-foreground">
                    未載入 PDF
                  </h2>
                  <p className="text-muted-foreground">
                    請提供有效的 PDF 檔案以檢視。
                  </p>
                </div>
              </div>
            }
          >
            <div
              ref={pageRef}
              key={`${displayMode}-${vw}x${vh}-${pageNumber}`}
              className="max-w-[100vw] max-h-[100vh] overflow-auto "
            >
              {/* border border-red-500 */}
              <div ref={pageWrapperRef} className="relative  flex gap-4 ">
                {/* border-4 border-black */}
                <Page
                  pageNumber={pageNumber}
                  {...pageDims}
                  scale={scale}
                  className="shadow-2xl"
                  renderTextLayer={!isGesturingRef.current}
                  renderAnnotationLayer={!isGesturingRef.current}
                  onLoadSuccess={() => {
                    // 使用 requestAnimationFrame 確保 DOM 已更新
                    requestAnimationFrame(() => {
                      const root = pageRef.current;
                      if (!root) return;

                      const canvas = root.querySelector(
                        ".react-pdf__Page__canvas"
                      );
                      if (canvas) {
                        const { width, height } =
                          canvas.getBoundingClientRect();
                        setPageSize({
                          width: Math.round(width),
                          height: Math.round(height),
                        });
                      }
                    });
                  }}
                />
                {pageSize.width > 0 && pageSize.height > 0 && (
                  <AnnotationLayer
                    pageNumber={pageNumber}
                    annotations={annotations}
                    scale={scale}
                    isDrawing={isDrawing}
                    config={annotationConfig}
                    onAnnotationCreate={handleAnnotationCreate}
                    onAnnotationDelete={handleAnnotationDelete}
                    onAnnotationUpdate={handleAnnotationUpdate}
                    containerWidth={pageSize.width}
                    containerHeight={pageSize.height}
                  />
                )}
                {/* Bookmark stickers for current page */}
                {bookmarks
                  .filter((b) => b.pageNumber === pageNumber)
                  .map((bookmark) => (
                    <BookmarkSticker
                      key={bookmark.id}
                      color={bookmark.color}
                      position={displayMode === "double" ? "left" : "right"}
                      onClick={() => setBookmarkPanelOpen(true)}
                    />
                  ))}

                {displayMode === "double" && pageNumber < numPages && (
                  <div className="relative">
                    <Page
                      pageNumber={pageNumber + 1}
                      {...pageDims}
                      scale={scale}
                      className="shadow-2xl"
                      renderTextLayer={!isGesturingRef.current}
                      renderAnnotationLayer={!isGesturingRef.current}
                      onLoadSuccess={() => {
                        // 使用 requestAnimationFrame 確保 DOM 已更新
                        requestAnimationFrame(() => {
                          const root = pageRef.current;
                          if (!root) return;

                          const canvas = root.querySelector(
                            ".react-pdf__Page__canvas"
                          );
                          if (canvas) {
                            const { width, height } =
                              canvas.getBoundingClientRect();
                            setPageSize({
                              width: Math.round(width),
                              height: Math.round(height),
                            });
                          }
                        });
                      }}
                    />
                    {pageSize.width > 0 && pageSize.height > 0 && (
                      <AnnotationLayer
                        pageNumber={pageNumber + 1}
                        annotations={annotations}
                        scale={scale}
                        isDrawing={isDrawing}
                        config={annotationConfig}
                        onAnnotationCreate={handleAnnotationCreate}
                        onAnnotationDelete={handleAnnotationDelete}
                        onAnnotationUpdate={handleAnnotationUpdate}
                        containerWidth={pageSize.width}
                        containerHeight={pageSize.height}
                      />
                    )}
                    {/* Bookmark stickers for next page in double mode */}
                    {bookmarks
                      .filter((b) => b.pageNumber === pageNumber + 1)
                      .map((bookmark) => (
                        <BookmarkSticker
                          key={bookmark.id}
                          color={bookmark.color}
                          position="right"
                          onClick={() => setBookmarkPanelOpen(true)}
                        />
                      ))}
                  </div>
                )}
              </div>
            </div>
          </Document>
        </div>
      </div>
      {/* Progress Bar */}
      {toolbarVisible && (
        <div className="absolute bottom-0 left-0 right-0 z-40 border-t bg-[hsl(var(--toolbar-bg))] p-2 backdrop-blur-sm hud">
          <div className="mx-auto flex max-w-4xl items-center gap-2">
            <span className="text-sm text-muted-foreground">
              {pageNumber} / {numPages}
            </span>
            <input
              type="range"
              min="1"
              max={numPages}
              value={pageNumber}
              onChange={(e) => goToPage(parseInt(e.target.value))}
              className="flex-1 cursor-pointer"
              style={{
                accentColor: "hsl(var(--primary))",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
